import { Upload, Calculator, TrendingUp, AlertCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';

const getActivityIcon = (type) => {
  switch (type) {
    case 'import':
      return <Upload className="text-blue-600" size={16} />;
    case 'calculation':
      return <Calculator className="text-green-600" size={16} />;
    case 'alert':
      return <AlertCircle className="text-orange-600" size={16} />;
    default:
      return <TrendingUp className="text-gray-600" size={16} />;
  }
};

const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function RecentActivity() {
  const { data, isLoading } = useQuery({
    queryKey: ['recent-activities'],
    queryFn: async () => {
      const response = await apiClient.get('/api/v1/dashboard/activities?limit=8');
      return response.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const activities = data?.data?.activities || [];
  const stats = data?.data?.stats || {};

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest updates from your portfolio</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-200 rounded animate-pulse" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse" />
                  <div className="h-3 bg-gray-200 rounded w-1/4 animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock size={20} className="text-blue-600" />
          Recent Activity
        </CardTitle>
        <CardDescription>
          {stats.parts_imported_this_week > 0 || stats.calculations_this_week > 0 ? (
            <span>
              {stats.parts_imported_this_week} parts imported, {stats.calculations_this_week} calculations this week
            </span>
          ) : (
            'Latest updates from your portfolio'
          )}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {activities.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No recent activities</p>
            <p className="text-sm mt-1">Import data or run calculations to see activity</p>
          </div>
        ) : (
          <div className="space-y-4">
            {activities.map((activity, idx) => (
              <div key={idx} className="flex items-start gap-3 group hover:bg-gray-50 p-2 rounded-lg transition-colors">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-white">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 font-medium">
                    {activity.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatTimestamp(activity.timestamp)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
